import React, { useState, useEffect, useCallback } from 'react';
import {
  getReligionStateLanguages, createReligionStateLanguage, updateReligionStateLanguage, deleteReligionStateLanguage,
  getReligiousReferenceTypes, createReligiousReferenceType, updateReligiousReferenceType, deleteReligiousReferenceType,
  getPlatformReligions,
} from '../services/adminService';
import { pageContainer, cardStyle, thStyle, tdStyle, badgeStyle, primaryBtn, secondaryBtn, inputStyle } from '../styles/theme';
import { useToast } from '../utils/useAdminPage';
import Toast from '../components/Toast';
import PageHeader from '../components/PageHeader';

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'grid', placeItems: 'center', zIndex: 1000 };
const modal: React.CSSProperties = { ...cardStyle, width: 480, maxHeight: '80vh', overflow: 'auto' };
const row: React.CSSProperties = { marginBottom: 14 };
const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: 4 };
const toggle: React.CSSProperties = { width: 18, height: 18, accentColor: 'hsl(var(--primary))' };
const delBtn: React.CSSProperties = { ...secondaryBtn, color: '#ef4444', borderColor: '#fecaca', fontSize: 12, padding: '6px 14px' };
const tabBar: React.CSSProperties = { display: 'flex', gap: 4, marginBottom: 28, borderBottom: '1px solid hsl(var(--border))' };
const tab = (a: boolean): React.CSSProperties => ({
  padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
  borderBottom: a ? '2px solid hsl(var(--primary))' : '2px solid transparent',
  background: 'transparent', color: a ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))', transition: 'all 150ms',
});
const langChip: React.CSSProperties = { display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'hsl(var(--primary)/0.10)', color: 'hsl(var(--primary))', marginRight: 4, marginBottom: 4 };

const TABS = ['Religion–State–Language', 'Religious Reference Types'] as const;
type Tab = typeof TABS[number];

const ReligionData = () => {
  const [activeTab, setActiveTab] = useState<Tab>('Religion–State–Language');
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [religions, setReligions] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const { toast, showToast } = useToast();

  /* RSL fields */
  const [rReligion, setRReligion] = useState('');
  const [rState, setRState] = useState('');
  const [rLanguages, setRLanguages] = useState('');

  /* RRT fields */
  const [tReligion, setTReligion] = useState('');
  const [tName, setTName] = useState('');
  const [tSort, setTSort] = useState(0);
  const [tActive, setTActive] = useState(true);

  const [filterReligion, setFilterReligion] = useState('');

  const fetchReligions = useCallback(async () => { const r = await getPlatformReligions(); setReligions(r); }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const items = activeTab === 'Religion–State–Language'
      ? await getReligionStateLanguages()
      : await getReligiousReferenceTypes();
    setData(items);
    setLoading(false);
  }, [activeTab]);

  useEffect(() => { fetchReligions(); }, [fetchReligions]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredData = data.filter(item => filterReligion ? item.religion === filterReligion : true);

  const openCreate = () => {
    setEditing(null);
    setRReligion(''); setRState(''); setRLanguages('');
    setTReligion(''); setTName(''); setTSort(0); setTActive(true);
    setShowModal(true);
  };

  const openEdit = (item: any) => {
    setEditing(item);
    if (activeTab === 'Religion–State–Language') {
      setRReligion(item.religion || ''); setRState(item.state || '');
      setRLanguages(Array.isArray(item.languages) ? item.languages.join(', ') : '');
    } else {
      setTReligion(item.religion || ''); setTName(item.name || '');
      setTSort(item.sortOrder ?? 0); setTActive(item.isActive ?? true);
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeTab === 'Religion–State–Language') {
        const langs = rLanguages.split(',').map(l => l.trim()).filter(Boolean);
        const payload = { religion: rReligion, state: rState, languages: langs };
        editing ? await updateReligionStateLanguage(editing.id, payload) : await createReligionStateLanguage(payload);
      } else {
        const payload = { religion: tReligion, name: tName, sortOrder: tSort, isActive: tActive };
        editing ? await updateReligiousReferenceType(editing.id, payload) : await createReligiousReferenceType(payload);
      }
      showToast(editing ? 'Updated successfully' : 'Created successfully');
      setShowModal(false); fetchData();
    } catch { showToast('Save failed', 'error'); }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      activeTab === 'Religion–State–Language'
        ? await deleteReligionStateLanguage(id) : await deleteReligiousReferenceType(id);
      showToast('Deleted'); fetchData();
    } catch { showToast('Delete failed', 'error'); }
  };

  return (
    <div style={pageContainer}>
      <Toast toast={toast} />
      <PageHeader breadcrumb="Dashboard › Religion Data" title="Religion Data Manager" subtitle="Manage religion–state–language mappings and religious reference types."
        onRefresh={fetchData} extraButtons={<button style={primaryBtn} onClick={openCreate}>+ New</button>} />

      <div style={tabBar}>
        {TABS.map(t => <button key={t} style={tab(activeTab === t)} onClick={() => setActiveTab(t)}>{t}</button>)}
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <select 
            style={{ ...inputStyle, width: 220 }} 
            value={filterReligion} 
            onChange={e => setFilterReligion(e.target.value)}
          >
            <option value="">All Religions</option>
            {religions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {activeTab === 'Religion–State–Language' ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>RELIGION</th><th style={thStyle}>STATE</th><th style={thStyle}>LANGUAGES</th><th style={thStyle}>ACTIONS</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
              : filteredData.length === 0 ? <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No entries found.</td></tr>
              : filteredData.map(item => (
                <tr key={item.id} style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                  <td style={tdStyle}><span style={badgeStyle}>{item.religion}</span></td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{item.state}</td>
                  <td style={tdStyle}>{(item.languages || []).map((l: string, i: number) => <span key={i} style={langChip}>{l}</span>)}</td>
                  <td style={tdStyle}><div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ ...secondaryBtn, fontSize: 12, padding: '6px 14px' }} onClick={() => openEdit(item)}>Edit</button>
                    <button style={delBtn} onClick={() => handleDelete(item.id)}>Delete</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>RELIGION</th><th style={thStyle}>NAME</th><th style={thStyle}>ACTIVE</th><th style={thStyle}>ORDER</th><th style={thStyle}>ACTIONS</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
              : filteredData.length === 0 ? <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No reference types found.</td></tr>
              : filteredData.map(item => (
                <tr key={item.id} style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                  <td style={tdStyle}><span style={badgeStyle}>{item.religion}</span></td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{item.name}</td>
                  <td style={tdStyle}><span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: item.isActive ? '#f0fdf4' : '#fef2f2', color: item.isActive ? '#15803d' : '#dc2626' }}>{item.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td style={tdStyle}>{item.sortOrder}</td>
                  <td style={tdStyle}><div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ ...secondaryBtn, fontSize: 12, padding: '6px 14px' }} onClick={() => openEdit(item)}>Edit</button>
                    <button style={delBtn} onClick={() => handleDelete(item.id)}>Delete</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div style={overlay} onClick={() => setShowModal(false)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: 'hsl(var(--foreground))' }}>
              {editing ? 'Edit' : 'Create'} {activeTab === 'Religion–State–Language' ? 'State–Language Mapping' : 'Reference Type'}
            </h3>

            {activeTab === 'Religion–State–Language' ? <>
              <div style={row}>
                <label style={lbl}>Religion *</label>
                <select style={inputStyle} value={rReligion} onChange={e => setRReligion(e.target.value)}>
                  <option value="">Select…</option>
                  {religions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div style={row}><label style={lbl}>State *</label><input style={inputStyle} value={rState} onChange={e => setRState(e.target.value)} /></div>
              <div style={row}><label style={lbl}>Languages (comma separated)</label><input style={inputStyle} value={rLanguages} onChange={e => setRLanguages(e.target.value)} placeholder="Hindi, English, Urdu" /></div>
            </> : <>
              <div style={row}>
                <label style={lbl}>Religion *</label>
                <select style={inputStyle} value={tReligion} onChange={e => setTReligion(e.target.value)}>
                  <option value="">Select…</option>
                  {religions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div style={row}><label style={lbl}>Name *</label><input style={inputStyle} value={tName} onChange={e => setTName(e.target.value)} /></div>
              <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
                <div style={{ flex: 1 }}><label style={lbl}>Sort Order</label><input type="number" style={inputStyle} value={tSort} onChange={e => setTSort(Number(e.target.value))} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 18 }}>
                  <input type="checkbox" checked={tActive} onChange={e => setTActive(e.target.checked)} style={toggle} /><label style={{ fontSize: 13 }}>Active</label>
                </div>
              </div>
            </>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button style={secondaryBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={primaryBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : editing ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReligionData;
